import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ProfileService } from '@shared/services/api/profile.service';
import { AuthenticationService } from '@shared/services/authentication.service';
import { PasswordRegExp } from '@shared/utils';
import { confirmPasswordValidator } from '@shared/validators/confirmPassword.validator';
import { forkJoin, switchMap, take, tap } from 'rxjs';
import { ThemeService } from '../services/theme.service';

@Component({
  selector: 'app-change-password',
  templateUrl: './change-password.component.html',
  styleUrl: './change-password.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChangePasswordComponent implements OnInit {
  private _destroyRef = inject(DestroyRef);
  private _cdr = inject(ChangeDetectorRef);
  isLight = false;
  isErrorChangePassword = false;
  isSuccessChangePassword = false;
  isLoading = false;
  isNewPassword = true;

  form = this._formBuilder.group({
    currentPassword: ['', [Validators.minLength(3), Validators.required]],
    passwords: this._formBuilder.group(
      {
        password: ['', [Validators.required, Validators.pattern(PasswordRegExp)]],
        confirmPassword: ['', [Validators.required, Validators.pattern(PasswordRegExp)]]
      },
      { validators: confirmPasswordValidator }
    )
  });

  get passwordsGroup() {
    return this.form.get('passwords') as FormGroup;
  }

  get passwordFormField() {
    return this.form.get('passwords')?.get('password');
  }

  constructor(
    private _router: Router,
    private _themeService: ThemeService,
    private _authenticationService: AuthenticationService,
    private _profileService: ProfileService,
    private _formBuilder: FormBuilder
  ) {}

  ngOnInit() {
    this._themeService.refresh$
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        switchMap(() => this._themeService.isDarkTheme$.pipe(tap((x) => (this.isLight = !x))))
      )
      .subscribe(() => this._cdr.markForCheck());

    this.form?.valueChanges.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((formValue) => {
      if (!this.isSuccessChangePassword) {
        const { currentPassword } = formValue;
        const newPassword = this.passwordFormField?.value || '';
        if (currentPassword && newPassword) {
          this.isNewPassword = currentPassword !== newPassword;
        } else {
          this.isNewPassword = true;
        }
        this._cdr.markForCheck();
      }
    });
  }

  onSaveNewPassword() {
    const currentPassword = this.form.get('currentPassword')?.value || '';
    const newPassword = this.passwordFormField?.value || '';

    if (currentPassword === newPassword) {
      this.isNewPassword = false;
      this.isSuccessChangePassword = false;
      this.isErrorChangePassword = false;
      return;
    }

    this.isLoading = true;
    this.isSuccessChangePassword = false;
    this.isErrorChangePassword = false;
    this.isNewPassword = true;

    forkJoin([
      this._authenticationService.changeUserPassword(newPassword, currentPassword),
      this._profileService.profile$.pipe(take(1))
    ])
      .pipe(
        switchMap(([, profile]) => this._profileService.save({ isNewUser: false }, profile.id)),
        tap({
          next: () => {
            this._profileService.refresh();
            this.isSuccessChangePassword = true;
            this.isErrorChangePassword = false;
            this.isNewPassword = true;
            this.isLoading = false;
            this.form.get('currentPassword')?.reset();
            this.passwordsGroup.reset();
            this._cdr.markForCheck();
            this._router.navigate(['/home']);
          },
          error: () => {
            this.isSuccessChangePassword = false;
            this.isErrorChangePassword = true;
            this.isNewPassword = true;
            this.isLoading = false;
            this._cdr.markForCheck();
          }
        })
      )
      .subscribe();
  }
}
